import { useLayoutEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { LogOut, PencilLine, UserCircle, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
	getCompanyData,
	getProfessionalData,
	getUserData,
} from '@/utils/cookies';
import { resolveCompanyPortalProfile } from '@/utils/companyPortal';
// import { useGetMeCompany } from '@/services/queries/company-auth';
import { Avatar } from './Avatar';
import { SidebarItem, useSidebar } from '@/context/SidebarContext';
import { E2E_SIDEBAR_USER } from '@/constants/e2eSchedulingTestIds';

const Sidebar = () => {
	const logoHeader =
		'https://res.cloudinary.com/dd5pbp2tb/image/upload/v1770986600/Plumaa_H_W_hribid.png';
	const {
		isExpanded,
		toggleSidebar,
		isMobile,
		tutorialForceSidebarOpen,
		tutorialHighlightNavItemsOnly,
		setTutorialNavHighlightRect,
		items,
		handleLogout,
		profilePath,
		showProfileFooter,
		showSidebarLogout,
		closeSidebar,
		compact,
	} = useSidebar();

	const tutorialNavListRef = useRef<HTMLUListElement | null>(null);

	useLayoutEffect(() => {
		if (!tutorialHighlightNavItemsOnly) {
			setTutorialNavHighlightRect(null);
			return;
		}
		const el = tutorialNavListRef.current;
		if (!el) return;

		const push = () => {
			const r = el.getBoundingClientRect();
			setTutorialNavHighlightRect({
				top: r.top,
				left: r.left,
				right: r.right,
				bottom: r.bottom,
			});
		};

		push();
		const ro = new ResizeObserver(push);
		ro.observe(el);
		window.addEventListener('resize', push);
		window.addEventListener('scroll', push, true);
		return () => {
			ro.disconnect();
			window.removeEventListener('resize', push);
			window.removeEventListener('scroll', push, true);
			setTutorialNavHighlightRect(null);
		};
	}, [
		tutorialHighlightNavItemsOnly,
		setTutorialNavHighlightRect,
		isExpanded,
		isMobile,
	]);

	const desktopRailExpanded =
		!isMobile && (isExpanded || tutorialForceSidebarOpen);

	/** Drawer mobile aberto pelo usuário ou forçado no passo “sidebar” do tutorial. */
	const mobileDrawerOpen = isExpanded || tutorialForceSidebarOpen;
	const location = useLocation();

	// Portal empresa com login desativado (/empresa/formulario não usa esta sidebar).
	// const isCompanyPortal =
	//   location.pathname.startsWith('/empresa/dashboard') ||
	//   location.pathname.startsWith('/empresa/interpretacao') ||
	//   location.pathname === '/empresa/login';
	// const { data: companyMe } = useGetMeCompany({ enabled: isCompanyPortal });
	const professional = getProfessionalData();
	const user = getUserData();
	const isActive = (path: string) => location.pathname.includes(path);

	const userInfo = (() => {
		// if (isCompanyPortal) {
		//   const company = companyMe ?? getCompanyData();
		//   const profile = resolveCompanyPortalProfile(company);
		//   return {
		//     name: profile.displayName || profile.headerTitle || 'Empresa',
		//     email: company?.billing_email ?? '',
		//     avatar: company?.logo_img_url ?? null,
		//   };
		// }
		const company = getCompanyData();
		const companyProfile = resolveCompanyPortalProfile(company);
		return {
			name: professional
				? [(professional as any).name_prefix, professional.first_name]
						.filter(Boolean)
						.join(' ')
				: user
				  ? user.first_name
				  : company
				    ? companyProfile.displayName || companyProfile.headerTitle
				    : 'Admin',
			email:
				professional?.email ||
				user?.email ||
				company?.billing_email ||
				'',
			avatar:
				professional?.profile_photo_url ||
				user?.profile_photo_url ||
				company?.logo_img_url ||
				null,
		};
	})();

	const ProfileAvatar = ({ size }: { size: 'sm' | 'md' }) => {
		const dimension = size === 'md' ? 'size-9' : 'size-8';
		const pencilSize = size === 'md' ? 14 : 12;

		return (
			<div className={cn('relative shrink-0', dimension)}>
				{userInfo.avatar ? (
					<Avatar
						className={cn('rounded-full', dimension)}
						img_url={userInfo.avatar}
						variant="soft"
					/>
				) : (
					<div
						className={cn(
							'flex items-center justify-center rounded-full bg-zinc-200 text-zinc-500',
							dimension,
						)}
					>
						<UserCircle size={size === 'md' ? 24 : 20} />
					</div>
				)}
				<div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-black/0 opacity-0 transition-all duration-200 group-hover:bg-black/50 group-hover:opacity-100">
					<PencilLine
						className="text-white"
						size={pencilSize}
						strokeWidth={2.5}
					/>
				</div>
			</div>
		);
	};

	const ProfileFooterLink = ({
		expanded,
		closeOnNavigate = false,
	}: {
		expanded: boolean;
		closeOnNavigate?: boolean;
	}) => {
		if (!profilePath) return null;

		const handleNavigate = () => {
			if (closeOnNavigate) closeSidebar();
		};

		if (expanded) {
			return (
				<Link
					to={profilePath}
					aria-label="Ir para o perfil"
					onClick={handleNavigate}
					className="group flex items-center gap-3 border-t border-zinc-100 bg-zinc-50/50 p-4 transition-colors hover:bg-zinc-200/80"
				>
					<ProfileAvatar size="md" />
					<div className="flex min-w-0 flex-col">
						<p className="truncate text-sm font-bold text-zinc-900 transition-colors group-hover:text-zinc-950">
							{userInfo.name}
						</p>
						<p className="truncate text-[11px] leading-tight text-zinc-500 transition-colors group-hover:text-zinc-600">
							{userInfo.email}
						</p>
					</div>
				</Link>
			);
		}

		return (
			<Link
				to={profilePath}
				aria-label="Ir para o perfil"
				onClick={handleNavigate}
				className="group mx-auto flex h-10 w-10 items-center justify-center"
			>
				<ProfileAvatar size="sm" />
			</Link>
		);
	};

	const NavItem = ({
		item,
		isMobileView = false,
	}: {
		item: SidebarItem;
		isMobileView?: boolean;
	}) => {
		const showLabels =
			isMobileView ||
			isExpanded ||
			(!isMobileView && tutorialForceSidebarOpen);

		if (item.type === 'section') {
			if (!showLabels) {
				return (
					<li
						className={cn(
							'flex justify-center first:hidden',
							compact ? 'px-3.5 pb-0.5 pt-2' : 'px-5 pb-1 pt-3',
						)}
					>
						<div
							className={cn(
								'bg-stone-200',
								compact ? 'h-px w-4' : 'h-px w-6',
							)}
						/>
					</li>
				);
			}
			return (
				<li
					className={cn(
						compact ? 'px-2.5 pb-0.5 pt-2.5' : 'px-3 pb-1 pt-4',
					)}
				>
					<span className="block text-[10px] font-black uppercase tracking-[0.16em] text-stone-400">
						{item.text}
					</span>
				</li>
			);
		}

		return (
			<li className="relative flex w-full items-center">
				{item.path && isActive(item.path) && (
					<div
						className={cn(
							'absolute left-0 z-10 rounded-r-full bg-zinc-900',
							compact ? 'h-5 w-0.5' : 'h-6 w-1',
						)}
					/>
				)}
				<Link
					to={item.path}
					onClick={() => {
						if (isMobileView) closeSidebar();
					}}
					className={cn(
						'flex items-center rounded-lg transition-all duration-200',
						showLabels
							? cn(
									'flex-1 justify-start',
									compact ? 'mx-2.5 p-2' : 'mx-3 p-2.5',
							  )
							: cn(
									'mx-auto justify-center p-0',
									compact ? 'h-9 w-9' : 'h-10 w-10',
							  ),
						item.path && isActive(item.path)
							? 'bg-stone-100 font-bold text-stone-900'
							: 'text-stone-500 hover:bg-stone-50',
					)}
				>
					{item.icon && (
						<span
							className={cn(
								'flex-shrink-0',
								item.path && isActive(item.path)
									? 'text-stone-900 [&_svg]:text-stone-900'
									: 'text-stone-700 [&_svg]:text-stone-700',
							)}
						>
							{item.icon}
						</span>
					)}
					{showLabels && (
						<span
							className={cn(
								'overflow-hidden whitespace-nowrap',
								item.icon &&
									(compact ? 'ml-2.5 text-sm' : 'ml-3'),
							)}
						>
							{item.text}
						</span>
					)}
				</Link>
			</li>
		);
	};

	const hasProfileFooterLink = Boolean(profilePath && showProfileFooter);
	const hasDesktopProfileInfoFooter =
		showProfileFooter && !profilePath && desktopRailExpanded;
	const hasSidebarFooterActions = showSidebarLogout || hasProfileFooterLink;

	if (isMobile) {
		return (
			<>
				{mobileDrawerOpen && (
					<div
						className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm"
						onClick={toggleSidebar}
					/>
				)}
				<div
					data-site-tutorial="sidebar-container"
					className={cn(
						'fixed left-0 top-0 z-[70] flex h-full w-72 flex-col bg-white shadow-2xl transition-transform duration-300',
						mobileDrawerOpen
							? 'translate-x-0'
							: '-translate-x-full',
					)}
				>
					<div className="flex items-center justify-between border-b border-zinc-100 bg-stone-900 p-6">
						<img
							src={logoHeader}
							className="h-8 w-auto"
							alt="Logo"
						/>
						<button
							onClick={toggleSidebar}
							className="p-1 text-white/70 hover:text-white"
						>
							<X size={24} />
						</button>
					</div>
					<nav
						className={cn(
							'relative z-[16] flex min-h-0 flex-1 flex-col overflow-y-auto',
							compact ? 'py-2.5' : 'py-4',
						)}
					>
						<ul
							ref={tutorialNavListRef}
							data-site-tutorial="sidebar-nav-items"
							className={cn(
								'space-y-1',
								tutorialHighlightNavItemsOnly &&
									'pointer-events-auto relative z-20',
							)}
						>
							{items.map((item) => (
								<NavItem
									key={item.text}
									item={item}
									isMobileView
								/>
							))}
						</ul>
					</nav>
					{hasSidebarFooterActions ? (
						<div className="relative z-[9] shrink-0 border-t border-zinc-100">
							{showSidebarLogout ? (
								<button
									type="button"
									onClick={() => {
										closeSidebar();
										handleLogout();
									}}
									data-testid={E2E_SIDEBAR_USER.LOGOUT}
									className="mx-3 mt-2 flex rounded-lg p-2 font-semibold text-zinc-500 hover:bg-red-50 hover:text-red-600"
								>
									<LogOut size={compact ? 18 : 20} />
									<span
										className={cn(
											compact ? 'ml-2.5 text-sm' : 'ml-3',
										)}
									>
										Sair
									</span>
								</button>
							) : null}
							{hasProfileFooterLink ? (
								<ProfileFooterLink expanded closeOnNavigate />
							) : null}
						</div>
					) : null}
				</div>
			</>
		);
	}

	return (
		<div
			data-site-tutorial="sidebar-container"
			className={cn(
				'relative z-[30] flex min-h-full flex-col border-r border-zinc-200 bg-white transition-all duration-300',
				desktopRailExpanded
					? compact
						? 'w-56'
						: 'w-64'
					: compact
					  ? 'w-[4.5rem]'
					  : 'w-20',
			)}
		>
			<nav
				className={cn(
					'relative z-[16] flex min-h-0 flex-1 flex-col justify-between',
					compact ? 'pt-2.5' : 'pt-4',
				)}
			>
				<ul
					ref={tutorialNavListRef}
					data-site-tutorial="sidebar-nav-items"
					className={cn(
						'space-y-1',
						tutorialHighlightNavItemsOnly &&
							'pointer-events-auto relative z-20',
					)}
				>
					{items.map((item) => (
						<NavItem key={item.text} item={item} />
					))}
				</ul>
				{hasSidebarFooterActions ? (
					<div
						className={cn(
							'flex w-full flex-col gap-1',
							hasProfileFooterLink && desktopRailExpanded
								? 'shrink-0 border-t border-zinc-100'
								: cn('pb-4', compact ? 'px-2.5' : 'px-3'),
						)}
					>
						{showSidebarLogout ? (
							<button
								type="button"
								onClick={handleLogout}
								data-testid={E2E_SIDEBAR_USER.LOGOUT}
								aria-label="Sair"
								className={cn(
									'flex items-center rounded-lg border-0 transition-all',
									desktopRailExpanded
										? cn(
												'mt-1.5 flex-1 justify-start text-zinc-500 hover:bg-red-50 hover:text-red-600',
												compact
													? 'mx-2.5 p-2'
													: 'mx-3 mt-2 p-2.5',
										  )
										: cn(
												'mx-auto justify-center p-0 text-zinc-500 hover:bg-red-50 hover:text-red-600',
												compact
													? 'h-9 w-9'
													: 'h-10 w-10',
										  ),
								)}
							>
								<LogOut size={compact ? 18 : 20} />
								{desktopRailExpanded && (
									<span
										className={cn(
											'font-semibold',
											compact
												? 'ml-2.5 text-sm'
												: 'ml-3 text-sm',
										)}
									>
										Sair
									</span>
								)}
							</button>
						) : null}
						{hasProfileFooterLink ? (
							<ProfileFooterLink expanded={desktopRailExpanded} />
						) : null}
					</div>
				) : (
					<div className={cn('pb-4', compact ? 'px-2.5' : 'px-3')} />
				)}
			</nav>
			{hasDesktopProfileInfoFooter ? (
				<div className="flex items-center gap-3 border-t border-zinc-100 bg-zinc-50/50 p-4">
					<ProfileAvatar size="md" />
					<div className="flex min-w-0 flex-col">
						<p className="truncate text-sm font-bold text-zinc-900">
							{userInfo.name}
						</p>
						<p className="truncate text-[11px] leading-tight text-zinc-500">
							{userInfo.email}
						</p>
					</div>
				</div>
			) : null}
		</div>
	);
};

export { Sidebar };
